import { Observable, Subject } from 'rxjs';

import { IFilterProvider } from '../components/filters/filter.interface';
import { GQLPageInfo, IGQLParams } from '../interfaces/graphql.interface';

export const PAGE_SIZE = 30;
export type TSortColumn = string;
export type TSortDirection = '' | 'ASC' | 'DESC';

export type TSortDirections = Record<string, TSortDirection>;

export type ISortableGrid = {
  moreRowsExists: boolean;
  //nextCursor: string;
  sort(sortBy: ISortEventArgs);
  readonly sortDirections: TSortDirections;
};

export type ISortEventArgs = {
  column: TSortColumn;
  direction: TSortDirection;
};

export class GridController<TFilter>
  implements ISortableGrid, IFilterProvider<TFilter>
{
  public moreRowsExists = false;

  private _params: IGQLParams<TFilter>;
  private _params$: Subject<IGQLParams<TFilter>>;
  private _isFiltersExpanded = true;

  private _page = 1;
  private _isLoading?: boolean;

  protected _sortDirections: TSortDirections = {};

  constructor() {
    this._params = {
      filter: undefined,
      orderby: '',
      direction: '',
    };
    this._params$ = new Subject<IGQLParams<TFilter>>();

    this._params$.subscribe({
      next: (p) => {
        console.log('grid params changed to:', p);
      },
      error: (error) => {
        this._isLoading = false;
        console.error(error);
      },
    });
  }

  protected init(
    filter: TFilter,
    _page: number,
    column: TSortColumn,
    direction?: TSortDirection,
  ) {
    this._params = {
      filter,
      orderby: column,
      direction: direction ?? '',
    };
    this._isFiltersExpanded =
      (localStorage.getItem('dashboard_pane_isFiltersExpanded') ?? 'true') ==
      'true';
  }

  private get page() {
    return this._page;
  }

  private set page(value: number) {
    if (value <= 0) {
      value = 1;
    }
    this._page = value;
    this._params$.next(this._params);
  }

  public get filter() {
    return this._params.filter;
  }

  public set filter(value: TFilter | undefined) {
    if (value) {
      this._isLoading = true;
      this._page = 1;
      this._params.filter = value;
      this._params.after = undefined;
      this._params$.next(this._params);
    }
  }

  public sort(sortBy: ISortEventArgs) {
    if (
      sortBy.column != this._params.orderby ||
      sortBy.direction != this._params.direction
    ) {
      this._isLoading = true;
      this._page = 1;
      this._params.orderby = sortBy.column;
      this._params.direction = sortBy.direction;
      this._params.after = undefined;
      this._params$.next(this._params);
    }
  }

  public get sortDirections(): TSortDirections {
    return this._sortDirections;
  }

  public get params$(): Observable<IGQLParams<TFilter>> {
    return this._params$;
  }

  public reload() {
    this._isLoading = true;
    this._page = 1;
    this.moreRowsExists = false;
    this._params.after = undefined;
    this._params$.next(this._params);
  }

  onScroll() {
    if (this.moreRowsExists) {
      this.page++;
    }
  }

  updatePage(page: GQLPageInfo) {
    this.moreRowsExists = page.hasNextPage;
    this._params.after = page.endCursor;
    this.filterDisabled = false;
    this._isLoading = false;
  }

  stopLoading() {
    this.filterDisabled = false;
    this._isLoading = false;
  }

  get isLoading(): boolean | undefined {
    return this._isLoading;
  }

  filterDisabled: boolean;

  get filterValues(): TFilter | undefined {
    return this.filter;
  }

  applyFilter(value: TFilter) {
    this.filterDisabled = true;
    this.filter = value;
  }

  get isFiltersExpanded(): boolean {
    return !this._isFiltersExpanded;
  }

  set isFiltersExpanded(value: boolean) {
    const newValue = !value;
    this._isFiltersExpanded = newValue;
    localStorage.setItem(
      'dashboard_pane_isFiltersExpanded',
      newValue.toString(),
    );
  }

  toggleFiltersPane() {
    this.isFiltersExpanded = !this.isFiltersExpanded;
  }
  //=======================================================
}
